import React, { useEffect, useState, useCallback } from "react";
import { PageHeader } from "./DashboardLayout";
import { api } from "@/lib/api";
import { CountUp } from "@/components/landing/Motion";
import { TrendingUp, MapPin, Smartphone, Headphones, BarChart2, Calendar } from "lucide-react";

const PRESETS = [
    { label: "1D", days: 1 },
    { label: "7D", days: 7 },
    { label: "30D", days: 30 },
];

function formatDateInput(d) {
    return d.toISOString().slice(0, 10);
}

export default function Analytics() {
    const [data, setData] = useState(null);
    const [preset, setPreset] = useState(30);
    const [customMode, setCustomMode] = useState(false);
    const [dateFrom, setDateFrom] = useState(() => formatDateInput(new Date(Date.now() - 30 * 86400000)));
    const [dateTo, setDateTo] = useState(() => formatDateInput(new Date()));
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            let params;
            if (customMode) {
                params = { date_from: dateFrom, date_to: dateTo };
            } else {
                params = { days: preset };
            }
            const r = await api.get("/analytics/overview", { params });
            setData(r.data);
        } catch (e) {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [preset, customMode, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const rangeLabel = customMode
        ? `${dateFrom} → ${dateTo}`
        : PRESETS.find(p => p.days === preset)?.label || `${preset}D`;

    if (loading && !data) return <div className="p-8 text-ink-muted text-[14px]">Loading…</div>;
    if (!data) return <div className="p-8 text-ink-muted text-[14px]">Could not load analytics.</div>;

    const totalCat = data.top_categories.reduce((s, c) => s + c.plays, 0);
    const maxPlays = Math.max(...(data.plays_by_day || []).map(d => d.plays), 1);

    return (
        <div data-testid="analytics-page">
            <PageHeader
                eyebrow="Analytics"
                title="The full listening picture."
                subtitle="Drop-off, devices, plays over time and more."
            />

            {/* Date range selector */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                {PRESETS.map(p => (
                    <button
                        key={p.days}
                        onClick={() => { setPreset(p.days); setCustomMode(false); }}
                        className={`px-4 py-2 rounded-full text-[13px] font-semibold border transition-all ${
                            !customMode && preset === p.days
                                ? "bg-ink text-cream-100 border-ink"
                                : "bg-white text-ink border-cream-300 hover:border-ink"
                        }`}
                    >
                        {p.label}
                    </button>
                ))}
                <button
                    onClick={() => setCustomMode(true)}
                    className={`px-4 py-2 rounded-full text-[13px] font-semibold border transition-all inline-flex items-center gap-1.5 ${
                        customMode
                            ? "bg-ink text-cream-100 border-ink"
                            : "bg-white text-ink border-cream-300 hover:border-ink"
                    }`}
                >
                    <Calendar size={12}/> Custom
                </button>
                {customMode && (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={dateFrom}
                            max={dateTo}
                            onChange={e => setDateFrom(e.target.value)}
                            className="border border-cream-300 rounded-xl px-3 py-2 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-coral/40"
                        />
                        <span className="text-ink-muted text-[12px]">to</span>
                        <input
                            type="date"
                            value={dateTo}
                            min={dateFrom}
                            max={formatDateInput(new Date())}
                            onChange={e => setDateTo(e.target.value)}
                            className="border border-cream-300 rounded-xl px-3 py-2 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-coral/40"
                        />
                    </div>
                )}
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {[
                    {label: `Plays (${rangeLabel})`, v: data.total_plays, c: "#FF5B3E", icon: Headphones},
                    {label: "Avg completion", v: data.avg_completion_pct, suffix: "%", c: "#2EE8B5", icon: TrendingUp},
                    {label: "Active today", v: data.active_listeners_today, c: "#3D5BFF", icon: Smartphone},
                    {label: "Cities", v: data.geo_top.length, c: "#FFD23F", icon: MapPin},
                ].map((k, i) => {
                    const Icon = k.icon;
                    return (
                        <div key={i} className="bg-white rounded-3xl p-5 border border-cream-300 shadow-soft">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{background: k.c + "22", color: k.c}}>
                                <Icon size={16}/>
                            </div>
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">{k.label}</p>
                            <p className="font-display text-[32px] font-bold leading-none mt-1.5"><CountUp to={k.v} suffix={k.suffix}/></p>
                        </div>
                    );
                })}
            </div>

            {/* Plays over time bar chart */}
            <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft mb-5">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-display text-[22px] font-semibold tracking-tight">Plays over time</h3>
                        <p className="text-[12.5px] text-ink-muted">Daily plays for the selected period.</p>
                    </div>
                    <BarChart2 size={18} className="text-ink-muted"/>
                </div>
                {data.total_plays === 0 ? (
                    <div className="h-40 flex items-center justify-center bg-cream-100 rounded-2xl border border-dashed border-cream-300">
                        <p className="text-[13px] text-ink-muted">No plays recorded in this period.</p>
                    </div>
                ) : (
                    <div className="h-44 flex items-end gap-[2px] overflow-hidden">
                        {(data.plays_by_day || []).map((d, i) => {
                            const h = Math.max(4, Math.round((d.plays / maxPlays) * 160));
                            return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                                    <div
                                        className="w-full rounded-t-sm bg-coral/80 group-hover:bg-coral transition-colors"
                                        style={{ height: `${h}px` }}
                                    />
                                    {/* Tooltip */}
                                    {d.plays > 0 && (
                                        <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-ink text-cream-100 text-[10px] px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                            {d.date}<br/>{d.plays} play{d.plays !== 1 ? "s" : ""}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {/* X-axis labels */}
                {data.plays_by_day && data.plays_by_day.length > 0 && (
                    <div className="flex justify-between mt-2 text-[10px] font-mono text-ink-muted">
                        <span>{data.plays_by_day[0]?.date}</span>
                        {data.plays_by_day.length > 2 && (
                            <span>{data.plays_by_day[Math.floor(data.plays_by_day.length / 2)]?.date}</span>
                        )}
                        <span>{data.plays_by_day[data.plays_by_day.length - 1]?.date}</span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Drop-off curve */}
                <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                    <div className="flex items-start justify-between mb-1">
                        <h3 className="font-display text-[22px] font-semibold tracking-tight">Drop-off curve</h3>
                        {!data.has_dropoff_data && (
                            <span className="text-[10px] bg-sunshine/40 text-ink px-2 py-1 rounded-full font-semibold">Estimated</span>
                        )}
                    </div>
                    <p className="text-[12.5px] text-ink-muted mb-5">
                        {data.has_dropoff_data
                            ? "% of listeners still listening at each completion point."
                            : "Showing estimated curve. Real data collects as users play articles."}
                    </p>
                    <div className="h-56 relative">
                        <svg viewBox="0 0 320 160" className="w-full h-full">
                            <defs>
                                <linearGradient id="grad" x1="0" x2="0" y1="0" y2="1">
                                    <stop offset="0%" stopColor="#FF5B3E" stopOpacity={data.has_dropoff_data ? 0.4 : 0.15}/>
                                    <stop offset="100%" stopColor="#FF5B3E" stopOpacity="0"/>
                                </linearGradient>
                            </defs>
                            {[0, 25, 50, 75, 100].map((y, i) => (
                                <line key={i} x1="0" x2="320" y1={i * 40} y2={i * 40} stroke="#EFE8D8" strokeWidth="1"/>
                            ))}
                            {/* Y-axis labels */}
                            {[100, 75, 50, 25, 0].map((v, i) => (
                                <text key={i} x="4" y={i * 40 + 10} fontSize="9" fill="#9E9085">{v}%</text>
                            ))}
                            {(() => {
                                const pts = data.drop_off.map((v, i) => `${(i / (data.drop_off.length - 1)) * 320},${160 - (v / 100) * 160}`).join(" L");
                                return (
                                    <>
                                        <path d={`M${pts} L320,160 L0,160 Z`} fill="url(#grad)"/>
                                        <path
                                            d={`M${pts}`}
                                            stroke={data.has_dropoff_data ? "#FF5B3E" : "#FFAA99"}
                                            strokeWidth="2.5"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeDasharray={data.has_dropoff_data ? "none" : "6 3"}
                                        />
                                    </>
                                );
                            })()}
                        </svg>
                    </div>
                    <div className="flex items-center justify-between mt-4 text-[11px] font-mono text-ink-muted">
                        <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                    </div>
                </div>

                {/* Devices */}
                <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                    <div className="flex items-start justify-between mb-5">
                        <h3 className="font-display text-[22px] font-semibold tracking-tight">Devices</h3>
                        {!data.has_device_data && (
                            <span className="text-[10px] bg-cream-200 text-ink-muted px-2 py-1 rounded-full font-semibold">No data yet</span>
                        )}
                    </div>
                    {data.has_device_data ? (
                        <div className="space-y-4">
                            {Object.entries(data.device_split).map(([k, v]) => (
                                <div key={k}>
                                    <div className="flex items-center justify-between text-[13px] mb-1.5">
                                        <span className="capitalize text-ink font-medium">{k}</span>
                                        <span className="font-mono font-bold">{v}%</span>
                                    </div>
                                    <div className="h-2 bg-cream-300 rounded-full overflow-hidden">
                                        <div className="h-full rounded-full transition-all duration-700" style={{
                                            width: `${v}%`,
                                            background: k === "mobile" ? "#FF5B3E" : k === "desktop" ? "#3D5BFF" : "#2EE8B5"
                                        }}/>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {["Mobile", "Desktop", "Tablet"].map((k, i) => (
                                <div key={k}>
                                    <div className="flex items-center justify-between text-[13px] mb-1.5">
                                        <span className="text-ink-muted font-medium">{k}</span>
                                        <span className="font-mono text-ink-muted">—</span>
                                    </div>
                                    <div className="h-2 bg-cream-200 rounded-full"/>
                                </div>
                            ))}
                            <p className="text-[11px] text-ink-muted mt-1">Device data collects when users play articles.</p>
                        </div>
                    )}
                </div>

                {/* Top articles */}
                <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                    <h3 className="font-display text-[22px] font-semibold tracking-tight mb-5">Top articles</h3>
                    {data.top_categories.length === 0 ? (
                        <div className="text-center py-8 bg-cream-100 rounded-2xl border border-dashed border-cream-300">
                            <p className="text-[13px] text-ink-muted">No articles played yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {data.top_categories.map((c, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between mb-1.5 text-[13.5px]">
                                        <span className="font-semibold text-ink truncate pr-4">{c.label}</span>
                                        <span className="font-mono text-ink-muted shrink-0">{c.plays.toLocaleString()}</span>
                                    </div>
                                    <div className="h-2.5 bg-cream-300 rounded-full overflow-hidden">
                                        <div className="h-full rounded-full transition-all duration-700" style={{width: `${(c.plays / totalCat) * 100}%`, background: c.color}}/>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Geo */}
                <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                    <h3 className="font-display text-[22px] font-semibold tracking-tight mb-5">Top cities</h3>
                    {data.geo_top.length === 0 ? (
                        <div className="text-center py-8 bg-cream-100 rounded-2xl border border-dashed border-cream-300">
                            <MapPin size={22} className="mx-auto mb-2 text-ink-muted"/>
                            <p className="text-[13px] text-ink-muted">Geography data coming soon.</p>
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {data.geo_top.map((g, i) => (
                                <li key={i} className="flex items-center gap-3">
                                    <span className="font-mono text-[11px] text-ink-muted w-5">#{i + 1}</span>
                                    <span className="flex-1 text-[14px] font-medium text-ink">{g.city}</span>
                                    <span className="text-[12px] font-bold text-coral">{g.share}%</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}


